import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useLoginMutation,
  useForgotPasswordMutation,
} from "../slices/usersApiSlice";
import { setCredentials } from "../slices/authSlice";
import { toast } from "react-toastify";

const LoginScreen = () => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPhone, setForgotPhone] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [login, { isLoading }] = useLoginMutation();

  const [
    forgotPassword,
    { isLoading: isForgotPasswordLoading },
  ] = useForgotPasswordMutation();

  const { userInfo } = useSelector((state) => state.auth);

  const redirect =
    new URLSearchParams(location.search).get("redirect") || "/home";

  // ============================================
  // Handle Already Authenticated User
  // ============================================

  useEffect(() => {
    if (!userInfo) {
      return;
    }

    if (userInfo.mustChangePassword) {
      navigate("/change-password", { replace: true });
      return;
    }

    navigate(redirect, { replace: true });
  }, [userInfo, navigate, redirect]);

  // ============================================
  // Login
  // ============================================

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!phone.trim() || !password) {
      toast.error("Please enter phone number and password");
      return;
    }

    try {
      const res = await login({
        phone: phone.trim(),
        password,
      }).unwrap();

      dispatch(setCredentials(res));

      if (res.mustChangePassword) {
        toast.info("Please change your temporary password");

        navigate("/change-password", {
          replace: true,
        });

        return;
      }

      navigate(redirect, {
        replace: true,
      });
    } catch (err) {
      const message =
        err?.data?.message ||
        err?.error ||
        "Login failed";

      switch (message) {
        case "Coaching center not assigned":
          toast.error(
            "Your account is not linked with a coaching center. Please contact administrator.",
          );
          break;

        case "Invalid phone number or password":
          toast.error("Invalid phone number or password");
          break;

        case "Company not resolved":
          toast.error(
            "Company could not be identified. Please access the login page using your company URL.",
          );
          break;

        default:
          toast.error(message);
      }
    }
  };

  // ============================================
  // Forgot Password
  // ============================================

  const forgotPasswordHandler = async (e) => {
    e.preventDefault();

    if (!forgotPhone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }

    try {
      const res = await forgotPassword({
        phone: forgotPhone.trim(),
      }).unwrap();

      toast.success(
        res?.message ||
          "Your password will be reset soon or contact the coaching center.",
      );

      // Close forgot password form
      setShowForgotPassword(false);

      // Clear phone number
      setForgotPhone("");
    } catch (err) {
      const message =
        err?.data?.message ||
        err?.error ||
        "Unable to submit password reset request";

      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 px-4 py-10">
      {/* CARD */}
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl px-6 sm:px-8 py-8">

        {/* HEADER */}
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Welcome Back
          </h2>

          <h5 className="text-white/70 font-bold sm:text-xl mt-1">
            Sign in to continue
          </h5>
        </div>

        {/* LOGIN FORM */}
        <form onSubmit={submitHandler} className="space-y-5">

          {/* PHONE */}
          <div className="flex flex-col gap-2 bg-white/5 p-3 sm:p-4 rounded-xl border border-white/10 backdrop-blur-sm">
            <label className="text-sm font-medium text-indigo-100 tracking-wide">
              Phone Number
            </label>

            <input
              type="tel"
              placeholder="Enter your phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              className="w-full h-11 sm:h-12 px-4 rounded-lg bg-white text-gray-800 placeholder-gray-400
                border border-transparent
                focus:border-indigo-500 focus:ring-2 focus:ring-indigo-400
                outline-none transition shadow-sm"
            />
          </div>

          {/* PASSWORD */}
          <div className="flex flex-col gap-2 bg-white/5 p-3 sm:p-4 rounded-xl border border-white/10 backdrop-blur-sm">
            <label className="text-sm font-medium text-indigo-100 tracking-wide">
              Password
            </label>

            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full h-12 rounded-lg bg-white text-gray-800 placeholder-gray-400
                border border-transparent
                focus:border-indigo-500 focus:ring-2 focus:ring-indigo-400
                outline-none transition shadow-sm px-4"
            />

            {/* SHOW / HIDE PASSWORD */}
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="self-start text-sm font-medium text-indigo-200
                hover:text-indigo-400 transition
                px-1 py-1"
            >
              {showPassword
                ? "Hide password 🙈"
                : "Show password 👁️"}
            </button>
          </div>

          {/* BUTTONS */}
          <div className="flex flex-col sm:flex-row gap-3 mt-10">

            {/* SIGN IN */}
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 h-12 rounded-xl font-semibold text-white
                bg-gradient-to-r from-cyan-500 via-sky-600 to-blue-700
                hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600
                shadow-lg hover:shadow-xl
                transition-all duration-200
                active:scale-[0.98]
                flex items-center justify-center
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="animate-pulse tracking-wide">
                  Signing In...
                </span>
              ) : (
                "Sign In"
              )}
            </button>

            {/* FORGOT PASSWORD */}
            <button
              type="button"
              onClick={() =>
                setShowForgotPassword((prev) => !prev)
              }
              className="flex-1 h-12 rounded-xl font-semibold text-white
                bg-white/10 border border-white/30
                hover:bg-white/20
                transition-all duration-200
                active:scale-[0.98]"
            >
              Forgot Password
            </button>
          </div>
        </form>

        {/* ============================================ */}
        {/* FORGOT PASSWORD FORM */}
        {/* ============================================ */}

        {showForgotPassword && (
          <div className="mt-6 p-4 rounded-xl bg-white/10 border border-white/20">

            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white">
                Forgot Password?
              </h3>

              <p className="text-sm text-white/70 mt-1">
                Enter your registered phone number. Your password
                reset request will be sent to the coaching center.
              </p>
            </div>

            <form
              onSubmit={forgotPasswordHandler}
              className="space-y-3"
            >
              <input
                type="tel"
                placeholder="Enter your phone number"
                value={forgotPhone}
                onChange={(e) => setForgotPhone(e.target.value)}
                autoComplete="tel"
                className="w-full h-12 px-4 rounded-lg bg-white text-gray-800
                  placeholder-gray-400
                  border border-transparent
                  focus:border-indigo-500
                  focus:ring-2 focus:ring-indigo-400
                  outline-none transition shadow-sm"
              />

              <button
                type="submit"
                disabled={isForgotPasswordLoading}
                className="w-full h-11 rounded-lg font-semibold text-white
                  bg-gradient-to-r from-emerald-500 to-green-600
                  hover:from-green-600 hover:to-emerald-700
                  transition
                  disabled:opacity-60
                  disabled:cursor-not-allowed"
              >
                {isForgotPasswordLoading
                  ? "Submitting..."
                  : "Submit Request"}
              </button>
            </form>
          </div>
        )}

        {/* INFORMATION */}
        <div className="text-center mt-6 text-white/70 text-sm">
          Students are created by your coaching center administrator.
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;